import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight, ChevronLeft } from "lucide-react";

// RTL-safe animated carousel: transform-based track (not scroll), autoplay 5s,
// pause on interaction, respects prefers-reduced-motion, touch swipe, no page h-scroll.
// visible: {mobile, tablet, desktop}. Fewer items than visible => plain grid, no controls.
const Carousel = ({ items, renderItem, testid, ariaLabel }) => {
  const [visible, setVisible] = useState(3);
  const [index, setIndex] = useState(0);
  const [reduced, setReduced] = useState(false);
  const paused = useRef(false);
  const touchX = useRef(null);

  useEffect(() => {
    const mqMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const mqMd = window.matchMedia("(min-width: 640px)");
    const mqLg = window.matchMedia("(min-width: 1024px)");
    const apply = () => {
      setReduced(mqMotion.matches);
      setVisible(mqLg.matches ? 3 : mqMd.matches ? 2 : 1);
    };
    apply();
    [mqMotion, mqMd, mqLg].forEach((mq) => mq.addEventListener("change", apply));
    return () => [mqMotion, mqMd, mqLg].forEach((mq) => mq.removeEventListener("change", apply));
  }, []);

  const maxIndex = Math.max(0, items.length - visible);
  const hasControls = items.length > visible;

  const next = useCallback(() => setIndex((i) => (i >= maxIndex ? 0 : i + 1)), [maxIndex]);
  const prev = useCallback(() => setIndex((i) => (i <= 0 ? maxIndex : i - 1)), [maxIndex]);

  useEffect(() => { setIndex((i) => Math.min(i, maxIndex)); }, [maxIndex]);

  useEffect(() => {
    if (reduced || !hasControls) return undefined;
    const id = setInterval(() => { if (!paused.current) next(); }, 5000);
    return () => clearInterval(id);
  }, [reduced, hasControls, next]);

  const pause = () => { paused.current = true; };
  const resume = () => { paused.current = false; };

  if (!items.length) return null;

  if (!hasControls) {
    return (
      <div className={`grid gap-5 ${visible === 3 ? "lg:grid-cols-3 sm:grid-cols-2" : visible === 2 ? "sm:grid-cols-2" : ""}`} data-testid={`${testid}-static`}>
        {items.map((item, i) => <Fragment key={i}>{renderItem(item, i)}</Fragment>)}
      </div>
    );
  }

  // RTL: first slide sits right; moving "next" shifts the track rightward (+X).
  const shift = (index * 100) / visible;

  return (
    <div
      className="relative"
      data-testid={testid}
      aria-label={ariaLabel}
      onPointerEnter={pause}
      onPointerLeave={resume}
      onFocus={pause}
      onBlur={resume}
      onTouchStart={(e) => { pause(); if (e.touches?.length) touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current !== null && e.changedTouches?.length) {
          const dx = e.changedTouches[0].clientX - touchX.current;
          if (Math.abs(dx) >= 40) (dx < 0 ? next() : prev());
        }
        touchX.current = null;
        resume();
      }}
    >
      <div className="overflow-hidden">
        <div
          className="flex transition-transform duration-700 ease-out"
          style={{ transform: `translateX(${shift}%)` }}
          data-testid={`${testid}-track`}
        >
          {items.map((item, i) => (
            <div key={i} className="shrink-0 px-2" style={{ width: `${100 / visible}%`, minWidth: `${visible === 1 ? 86 : 100 / visible}%` }}>
              {renderItem(item, i)}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-6 flex items-center justify-center gap-3">
        <button type="button" onClick={prev} aria-label="السابق" data-testid={`${testid}-prev`}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 text-slate-600 transition-colors hover:border-[#C80000] hover:bg-[#C80000] hover:text-white">
          <ChevronRight className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-1.5" dir="ltr">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button key={i} type="button" onClick={() => setIndex(i)} aria-label={`شريحة ${i + 1}`} data-testid={`${testid}-dot-${i}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-6 bg-[#C80000]" : "w-1.5 bg-slate-300 hover:bg-slate-400"}`} />
          ))}
        </div>
        <button type="button" onClick={next} aria-label="التالي" data-testid={`${testid}-next`}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 text-slate-600 transition-colors hover:border-[#C80000] hover:bg-[#C80000] hover:text-white">
          <ChevronLeft className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default Carousel;
